import { nanoid } from 'nanoid';
import type { StateCreator } from 'zustand';
import { ok, type Result } from 'neverthrow';
import {
  CommandEffectKind,
} from '../lib/commands';
import type {
  CommandError,
  CommandEffect,
  CommandResult,
  GraphCommand,
} from '../lib/commands';
import { match } from 'ts-pattern';
import type { GraphState } from '.';
import type { StoreSlice } from '@/store';
import { DirtyType } from '@/types';
import { type GraphId, GraphRefType } from '../types';

export type CommandEnvelope = {
  id: string;
  timestamp: number;
  label?: string;
  command: GraphCommand;
  inverse?: GraphCommand;
};

export type HistoryState = {
  past: CommandEnvelope[];
  future: CommandEnvelope[];
};

export interface CommandSlice {
  applyCommand: (
    command: GraphCommand,
    label?: string,
  ) => Result<CommandResult, CommandError>;
  undo: () => Result<CommandResult, CommandError> | null;
  redo: () => Result<CommandResult, CommandError> | null;
  clearHistory: () => void;
  getHistory: () => HistoryState;
}

export const createCommandSlice =
  (
    global: StoreSlice,
  ): StateCreator<GraphState, [['zustand/immer', never]], [], CommandSlice> =>
  (_set, _get) => {
    const getHistory = (): HistoryState => {
      const graphApi = _get().graph;
      if (!graphApi) {
        return { past: [], future: [] };
      }
      return graphApi.getHistory();
    };

    const resolveOwningFieldEntryId = (
      graphId: GraphId,
    ): string | undefined => {
      let currentGraphId: GraphId | undefined = graphId;

      while (currentGraphId) {
        const ref = global.graphs.getRef(currentGraphId);

        if (ref.kind === GraphRefType.Field) {
          return ref.fieldId;
        }

        currentGraphId = ref.graphId;
      }

      return undefined;
    };

    /**
     * Unified interface for applying and reevaluation.
     */
    const applyGraph = (result: CommandResult) => {
      _get().graph.setGraph(result.graph);
      _set((state) => {
        state.graph = { ...state.graph };
      });
      // We remove dirty nodes that do no longer exist in the graph,
      // this may happen when invoking delete commands.
      const dirtyNodes = result.dirty.nodes.filter((nodeId) =>
        Boolean(result.graph.nodes[nodeId]),
      );
      // Queue the subgraph with the dirty nodes as sources for reevaluation.
      _get().graph.enqueueGraph(dirtyNodes);

      // In order for possible dependencies to be picked up we need to mark the owning entry as dirty.
      // This will trigger a complete compilation of which the result will subsequently be scanned for dependencies.
      const fieldEntryId = resolveOwningFieldEntryId(_get().graph.getId());
      if (fieldEntryId) {
        global.entries.addDirty(fieldEntryId, DirtyType.Underlying);
      }
    };

    const applyCommandEffects = (effects?: CommandEffect[]) => {
      if (!effects || effects.length === 0) return;

      effects.forEach((effect) => {
        match(effect)
          .with({ kind: CommandEffectKind.DeleteGraph }, ({ graphId }) => {
            _get().graph.deleteGraph(graphId);
          })
          .with({ kind: CommandEffectKind.RestoreGraph }, ({ graphId }) => {
            _get().graph.restoreGraph(graphId);
          })
          .exhaustive();
      });
    };

    return {
      getHistory: () => getHistory(),

      applyCommand: (command, label) => {
        const result = command.apply(_get().graph.get());
        if (result.isErr()) return result;

        applyCommandEffects(result.value.effects);
        applyGraph(result.value);
        const envelope: CommandEnvelope = {
          id: nanoid(10),
          timestamp: Date.now(),
          label,
          command,
          inverse: result.value.inverse,
        };

        _get().graph.pushHistory(envelope);

        return ok(result.value);
      },

      undo: () => {
        const envelope = _get().graph.undoHistory();
        if (!envelope || !envelope.inverse) {
          return null;
        }

        const result = envelope.inverse.apply(_get().graph.get());
        if (result.isErr()) return result;

        applyCommandEffects(result.value.effects);
        applyGraph(result.value);

        return ok(result.value);
      },

      redo: () => {
        const envelope = _get().graph.redoHistory();
        if (!envelope) {
          return null;
        }

        const result = envelope.command.apply(_get().graph.get());
        if (result.isErr()) return result;

        const updatedEnvelope = {
          ...envelope,
          inverse: result.value.inverse,
        };

        applyCommandEffects(result.value.effects);
        applyGraph(result.value);

        const history = getHistory();
        const past = history.past.slice();
        const lastIndex = past.length - 1;
        if (lastIndex >= 0 && past[lastIndex]?.id === updatedEnvelope.id) {
          past[lastIndex] = updatedEnvelope;
        }
        _get().graph.setHistory({ past, future: history.future });

        return ok(result.value);
      },

      clearHistory: () => {
        _get().graph.clearHistory();
      },
    };
  };
